import { createFileRoute, Link } from "@tanstack/react-router";
import { Gift, Check, Crown, ShieldCheck, Sparkles, Star, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LaunchCounter } from "@/components/launch-counter";
import { LaunchPopup } from "@/components/launch-popup";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/lib/use-auth";

const CHEF_FAQS: { q: string; a: string }[] = [
  {
    q: "Serve la Partita IVA?",
    a: "No. Per registrarti su ChefConnect non è richiesta la Partita IVA. Puoi creare il tuo account, verificare il profilo e pubblicare i tuoi annunci una volta approvato. Ogni chef resta responsabile della gestione dei propri obblighi fiscali e amministrativi.",
  },
  {
    q: "Posso iscrivermi gratis?",
    a: "Sì. Creare il profilo e ricevere richieste è e resterà gratuito. Premium è una scelta, non un obbligo: serve solo a chi vuole più visibilità.",
  },
  {
    q: "Non sono uno chef stellato. Posso iscrivermi lo stesso?",
    a: "Sì. ChefConnect è aperto a personal chef, cuochi a domicilio, professionisti della ristorazione e appassionati con vera esperienza. Conta la qualità del tuo lavoro, non il titolo.",
  },
  {
    q: "Quando inizierò a ricevere richieste?",
    a: "Dipende molto dal profilo. Foto curate, bio chiara, specialità e prezzi visibili fanno arrivare i primi contatti già nelle prime settimane. Più il profilo è completo, più sei vicino al primo cliente.",
  },
  {
    q: "E se non ricevo richieste?",
    a: "Nessun problema: il profilo resta gratuito e nessuno ti addebita nulla. Possiamo aiutarti a migliorarlo o suggerirti come renderlo più efficace. Restare iscritto non costa.",
  },
  {
    q: "Posso cancellare il mio profilo quando voglio?",
    a: "Sì, in qualsiasi momento, dalla dashboard. Niente penali, niente vincoli: il tuo profilo è tuo.",
  },
  {
    q: "Cosa succede subito dopo la registrazione?",
    a: "Ti chiediamo qualche informazione sul tuo lavoro, alcune foto e le tue specialità. Appena il profilo è pubblicato sei visibile ai clienti della tua zona. I primi 100 chef ricevono automaticamente 30 giorni di Premium gratis e il badge Founder permanente.",
  },
  {
    q: "Come funziona Premium?",
    a: "Premium ti fa vedere prima e più spesso (priorità nei risultati, evidenza in mappa, presenza in homepage per Gold). Non garantisce richieste, ma aumenta la visibilità: il resto lo fa la qualità del tuo profilo.",
  },
  {
    q: "Ci sono commissioni sulle prenotazioni?",
    a: "No. ChefConnect non trattiene nulla sulle serate. Concordi prezzo e dettagli direttamente con il cliente: quello che pattuisci è quello che incassi.",
  },
];

export const Route = createFileRoute("/diventa-chef")({
  head: () => ({
    meta: [
      { title: "Diventa Chef su ChefConnect — 30 giorni Premium gratis" },
      { name: "description", content: "Iscriviti gratis come chef privato. I primi 100 chef ricevono 30 giorni di Premium gratis, badge Founder permanente e priorità nelle ricerche." },
      { property: "og:title", content: "Offerta di lancio — Diventa Chef gratis" },
      { property: "og:description", content: "30 giorni di Premium gratis per i primi 100 chef. Nessuna carta richiesta." },
      { property: "og:url", content: "https://chefconnectitalia.com/diventa-chef" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
      { name: "twitter:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/diventa-chef" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: CHEF_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
  component: BecomeChefPage,
});

function BecomeChefPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard/profilo" : "/auth";
  const ctaSearch = user ? undefined : { mode: "signup" as const, redirect: "/dashboard/profilo" };

  return (
    <>
      <LaunchPopup />
      <section className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-gold px-3 py-1 text-xs font-bold uppercase tracking-wider text-gold-foreground">
              <Gift className="h-3.5 w-3.5" /> Offerta di lancio
            </span>
            <h1 className="mt-5 font-display text-5xl leading-tight sm:text-6xl">
              Fatti trovare dai<br /><span className="text-gold italic">clienti giusti.</span>
            </h1>
            <p className="mt-5 text-lg text-muted-foreground">
              Iscriversi è gratis, sempre. I primi 100 chef ricevono in più
              <strong> 30 giorni di Premium</strong> e il <strong>badge Founder</strong> permanente.
              Nessuna carta di credito, nessun vincolo.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              {[
                ["Profilo gratis, per sempre", Check],
                ["Nessuna commissione sulle serate", Check],
                ["30 giorni di Premium Silver per i primi 100 chef", Crown],
                ["Priorità nei risultati di ricerca", Star],
                ["Badge Founder permanente sul profilo", Sparkles],
                ["Cancellazione in qualsiasi momento", Check],
              ].map(([text, Icon]) => {
                const I = Icon as React.ComponentType<{ className?: string }>;
                return (
                  <li key={text as string} className="flex items-center gap-2">
                    <I className="h-4 w-4 text-gold" />
                    <span>{text as string}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold/90">
                <Link to={ctaTo as never} search={ctaSearch as never}>Crea il tuo profilo — gratis</Link>
              </Button>
              <span className="text-xs text-muted-foreground">
                In 30 secondi con email o Google. Nessuna carta.
              </span>
            </div>
          </div>

          <div className="space-y-6">
            <LaunchCounter variant="block" />
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">Come funziona</h3>
              <ol className="mt-4 space-y-3 text-sm">
                <li className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground text-xs font-bold text-background">1</span><div><strong>Crea il tuo account</strong> in 30 secondi con email o Google.</div></li>
                <li className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground text-xs font-bold text-background">2</span><div><strong>Completa il profilo chef</strong>: bio, foto, specialità, prezzi.</div></li>
                <li className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground text-xs font-bold text-background">3</span><div><strong>Vai online</strong> e ricevi richieste dai clienti della tua zona.</div></li>
              </ol>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border bg-secondary/30 p-5 text-sm">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> 0 commissioni</span>
              <span className="inline-flex items-center gap-2"><Crown className="h-4 w-4 text-gold" /> 30 giorni Premium</span>
              <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> Badge Founder</span>
            </div>
          </div>
        </div>
      </section>

      {/* RASSICURAZIONI / PAURE */}
      <section className="border-y border-border bg-secondary/30">
        <div className="container mx-auto max-w-5xl px-4 py-16 sm:py-20">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Prima di iscriverti</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">Tutto quello che probabilmente ti stai chiedendo.</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Risposte chiare. Niente piccoli caratteri.
            </p>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {CHEF_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`chef-q${i}`}>
                <AccordionTrigger className="text-left font-medium">
                  <span className="flex items-start gap-3">
                    <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    {f.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pl-7 text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to={ctaTo as never} search={ctaSearch as never}>Inizia gratis ora</Link>
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">
              Nessuna carta. Cancelli quando vuoi.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}